import styles from './styles.module.css'

import { AlertDialog } from '@radix-ui/themes'
import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import { KILL_URLS } from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  getTrackShares,
  trackShare,
} from '@disco/state-management/actions/TrackShareActions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { trackSharesWithContacts } from '@disco/state-management/selectors/shares'
import { pushUINotification } from '@disco/utils/notifications'

import type { Track } from 'types/track'

import { RecipientInput } from 'components/common/recipient-input'
import type { RecipientTag } from 'components/common/recipient-input'
import {
  Box,
  Button as DsButton,
  Flex as DsFlex,
  Link,
  Spinner,
  Switch,
  Table,
  Text,
} from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { buildShareRecipients, isSameRecipient } from './share-recipients'

interface TrackAssignTabProps {
  track: Track
  onClose?: () => void
}

const MAX_RECIPIENTS_LENGTH = 200

/**
 * "Assign a URL to a contact" panel. Generates a per-recipient share link
 * without emailing, and lists/manages previously assigned links (copy,
 * enable/disable, access stats).
 */
export const TrackAssignTab: React.FC<TrackAssignTabProps> = ({
  track,
  onClose,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isDisableLinksEnabled = useFeatureEnabled(KILL_URLS)

  const { sharesLoaded, sharesItems } = useSelector(state =>
    trackSharesWithContacts(state, { id: track.id }),
  )

  const [picked, setPicked] = useState<RecipientTag[]>([])
  const [isAssigning, setIsAssigning] = useState(false)
  const [pendingDisable, setPendingDisable] = useState<any>(null)
  const [togglingShareId, setTogglingShareId] = useState<number | null>(null)

  useEffect(() => {
    if (picked.length > MAX_RECIPIENTS_LENGTH) {
      mixpanel.sendPureEvent(
        `User attempted to assign to more than ${MAX_RECIPIENTS_LENGTH} recipients`,
        { recipient_count: picked.length },
      )
    }
  }, [picked.length])

  const isOverRecipientLimit = picked.length > MAX_RECIPIENTS_LENGTH

  const addPicked = (
    email?: string,
    name?: string,
    contactId?: number,
    meta?: { type?: 'email' | 'user' | 'business' },
  ) => {
    const trimmedEmail = email?.trim() || ''
    const trimmedName = name?.trim() || ''
    if (!trimmedEmail && !trimmedName && contactId == null) return

    setPicked(prev => {
      const candidate = { contactId, type: meta?.type, email: trimmedEmail }
      const isDuplicate = prev.some(r => isSameRecipient(r, candidate))
      if (isDuplicate) return prev
      return [
        ...prev,
        {
          id: `${Date.now()}-${Math.random()}`,
          email: trimmedEmail || undefined,
          name: trimmedName || undefined,
          contactId,
          isValid: true,
          type: meta?.type,
        },
      ]
    })
  }

  const removePicked = (id: string) => {
    setPicked(prev => prev.filter(r => r.id !== id))
  }

  const handleAssign = async () => {
    if (picked.length === 0 || isAssigning || isOverRecipientLimit) return
    setIsAssigning(true)

    try {
      // One trackShare call per recipient — each gets its own share_id, so
      // disabling one recipient's link doesn't disable everyone else's. Each
      // recipient is removed from `picked` as soon as its own call succeeds
      // (not only after the whole loop finishes), so a later failure can't
      // leave already-assigned recipients in the list to be re-sent — that
      // would create duplicate share links for them on retry.
      //
      // noPlaylistShareNotification suppresses trackShare's own per-call
      // toast (see TrackShareActions.ts) — otherwise N recipients would
      // stack N "Generated share URL!" toasts. One aggregate toast is
      // pushed below once the whole loop succeeds instead.
      let successCount = 0
      for (const recipient of picked) {
        const result = await dispatch(
          trackShare(track, buildShareRecipients([recipient]) as any, {
            manual: true,
            noPlaylistShareNotification: true,
          }),
        )
        successCount += 1
        mixpanel.sendPureEvent('Assign share', {
          'Track ID': track.id,
          Type: 'Track',
          Recipients: [
            ...new Set(
              (result?.recipients || []).map(r => r.id).filter(Boolean),
            ),
          ],
        })
        setPicked(prev => prev.filter(r => r.id !== recipient.id))
      }
      pushUINotification({
        message: `Generated ${successCount} share ${
          successCount > 1 ? 'URLs' : 'URL'
        }!`,
        level: 'success',
      })
    } catch (err) {
      pushUINotification({
        message: err instanceof Error ? err.message : 'Failed to assign URL',
        level: 'error',
      })
    } finally {
      setIsAssigning(false)
    }
  }

  const handleCopy = async (row: any) => {
    if (row.disabled) return

    try {
      await navigator.clipboard.writeText(row.recipient_view_url)
      pushUINotification({ message: 'Copied share URL!', level: 'success' })
      mixpanel.sendPureEvent('Copy assign share url', {
        'Track ID': track.id,
        'Contact ID': row.contact?.id,
        ...(typeof row.id === 'number' ? { 'User ID': row.id } : {}),
      })
    } catch {
      pushUINotification({
        message: 'Could not copy share URL!',
        level: 'error',
      })
    }
  }

  // No optimistic local override of `disabled` — the Switch reads straight
  // from the Redux-backed `row.disabled` (refreshed below on success), so a
  // failed request just leaves it showing the real, unchanged server state
  // instead of getting stuck on a flipped value that never reconciles.
  const setShareId = async (shareId: number, disabled: boolean) => {
    setTogglingShareId(shareId)
    try {
      if (disabled) {
        await api.disableShare(shareId)
        pushUINotification({ message: 'Link disabled!', level: 'success' })
      } else {
        await api.enablePlaylistShare(shareId)
        pushUINotification({ message: 'Link re-enabled!', level: 'success' })
      }
      dispatch(getTrackShares(track.id))
    } catch {
      pushUINotification({
        message: `Sorry! Failed to ${disabled ? 'disable' : 're-enable'} link.`,
        level: 'error',
      })
    } finally {
      setTogglingShareId(null)
    }
  }

  const handleToggleRequested = (row: any) => {
    if (row.disabled) {
      setShareId(row.share_id, false)
    } else {
      setPendingDisable(row)
    }
  }

  const handleConfirmDisable = () => {
    if (!pendingDisable) return
    const row = pendingDisable
    setPendingDisable(null)
    setShareId(row.share_id, true)
  }

  const handleUserClick = (row: any) => {
    if (row.contact?.id) {
      window.open(`/contacts/${row.contact.id}`, '_blank')
      dispatch(sendMixpanelEvent('Access Contacts via share list contact'))
    }
  }

  const handleOpenStats = () => {
    navigate(`/explorer/tracks/${track.id}/stats`)
    dispatch(sendMixpanelEvent('Access track stats via share modal'))
  }

  return (
    <>
      <Box>
        <Box mb="3">
          <RecipientInput
            recipients={picked}
            onAdd={addPicked}
            onRemove={removePicked}
            aria-label="Assign URL recipients"
            allowCreateRecipient
            includeBlankEmails
            includeBusinesses
            includeBriefs={false}
            label="Enter a name or email address, or search for contacts, mailing lists, or businesses"
          />
        </Box>

        {isOverRecipientLimit && (
          <Box mb="3">
            <Text size="2" color="red">
              You&apos;re assigning to {picked.length} recipients — please
              reduce your recipient list to under {MAX_RECIPIENTS_LENGTH}.
            </Text>
          </Box>
        )}

        <DsFlex mb="4" justify="end">
          <DsButton
            onClick={handleAssign}
            disabled={
              picked.length === 0 || isAssigning || isOverRecipientLimit
            }
          >
            {isAssigning && <Spinner size="1" />}
            <span>
              {isAssigning
                ? 'Generating'
                : picked.length > 1
                ? `Assign ${picked.length} URLs`
                : 'Assign URL'}
            </span>
          </DsButton>
        </DsFlex>
      </Box>

      <Box className={styles.scrollArea}>
        {!sharesLoaded ? (
          <Box mb="3" p="4">
            <Text size="2" color="gray" align="center" as="div">
              Loading URLs…
            </Text>
          </Box>
        ) : sharesItems && sharesItems.length > 0 ? (
          <Table.Root variant="surface" size="1">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>URL</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Active</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sharesItems.map(row => {
                const shareDisabled = row.disabled
                return (
                  <Table.Row key={row.id}>
                    <Table.Cell>
                      <Text
                        size="2"
                        className={clsx(
                          styles.overflowCell,
                          shareDisabled && styles.disabledRow,
                          row.contact?.id && styles.clickableCell,
                        )}
                        onClick={
                          row.contact?.id
                            ? () => handleUserClick(row)
                            : undefined
                        }
                        title={row.contact?.id ? 'View in contacts' : undefined}
                      >
                        {row.contact?.name || row.name || '—'}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        size="2"
                        color="gray"
                        className={clsx(shareDisabled && styles.disabledRow)}
                      >
                        {row.email}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <DsButton
                        variant="soft"
                        color="blue"
                        size="1"
                        disabled={shareDisabled}
                        onClick={() => handleCopy(row)}
                        aria-label={`Copy URL for ${row.email || row.name}`}
                      >
                        Copy URL
                      </DsButton>
                    </Table.Cell>
                    <Table.Cell>
                      <Switch
                        checked={!shareDisabled}
                        disabled={
                          !isDisableLinksEnabled ||
                          togglingShareId === row.share_id
                        }
                        onCheckedChange={() => handleToggleRequested(row)}
                        aria-label={
                          shareDisabled
                            ? `Enable link for ${row.email || row.name}`
                            : `Disable link for ${row.email || row.name}`
                        }
                      />
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>
        ) : (
          <Box mb="3" p="4">
            <Text size="2" color="gray" align="center" as="div">
              No URLs assigned yet. Pick a contact above to generate one.
            </Text>
          </Box>
        )}
      </Box>

      <DsFlex justify="between" align="center" mt="4">
        <Box>
          {!sharesLoaded ? (
            <Text size="2" color="gray">
              Loading…
            </Text>
          ) : sharesItems && sharesItems.length > 0 ? (
            <Text size="2" color="gray">
              View{' '}
              <Link
                href="#"
                onClick={e => {
                  e.preventDefault()
                  handleOpenStats()
                }}
              >
                Access Stats
              </Link>
              .
            </Text>
          ) : null}
        </Box>
        <DsButton variant="soft" color="gray" onClick={onClose}>
          Done
        </DsButton>
      </DsFlex>

      <AlertDialog.Root
        open={pendingDisable !== null}
        onOpenChange={open => {
          if (!open) setPendingDisable(null)
        }}
      >
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>Disable this link?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            The recipient will lose access via this URL. You can re-enable it
            later.
          </AlertDialog.Description>
          <DsFlex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <DsButton variant="soft" color="gray">
                Cancel
              </DsButton>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <DsButton
                variant="solid"
                color="red"
                onClick={handleConfirmDisable}
              >
                Disable link
              </DsButton>
            </AlertDialog.Action>
          </DsFlex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </>
  )
}
